import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Link2, Unlink, Trash2, Building2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";

const TYPE_LABEL = { hospital: "Hospital", pharmacy: "Pharmacy", hospital_pharmacy: "Hospital + Pharmacy" };

export default function Groups() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get("/owner/groups"); setData(data); }
    catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { toast.error("Enter a group name."); return; }
    setBusy(true);
    try {
      await api.post("/owner/groups", { name: name.trim(), business_ids: picked });
      toast.success("Group created."); setOpen(false); setName(""); setPicked([]); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const link = async (groupId, businessId) => {
    if (!window.confirm("Link these businesses? Patient identity and authorized referrals can be shared. Staff permissions and historical records remain unchanged.")) return;
    try { await api.post(`/owner/groups/${groupId}/link`, { business_id: businessId }); toast.success("Business linked."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unlink = async (groupId, businessId) => {
    if (!window.confirm("Unlink this business? Its data stays fully intact — only identity sharing stops.")) return;
    try { await api.post(`/owner/groups/${groupId}/unlink`, { business_id: businessId }); toast.success("Unlinked (history preserved)."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const dissolve = async (groupId) => {
    if (!window.confirm("Dissolve this group? All members are unlinked; no data is deleted.")) return;
    try { await api.delete(`/owner/groups/${groupId}`); toast.success("Group dissolved."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div data-testid="groups-page">
      <PageHeader overline="Organization" title="Business Groups"
        subtitle="Link otherwise-independent businesses (e.g. a hospital and a pharmacy) so they share patient identity while linked. Unlinking preserves each business's full history — no data is ever moved or deleted."
        actions={<button onClick={() => setOpen(true)} className={primaryBtnCls} data-testid="add-group-btn"><Plus className="h-4 w-4" /> New group</button>} />

      {error && <ErrorState message={error} />}
      {!data ? <Loading /> : (
        <div className="space-y-6">
          {data.items.length === 0 ? (
            <div className="panel"><EmptyState title="No groups yet" subtitle="Create a group to link a hospital and a pharmacy for shared patient identity." testid="groups-empty" /></div>
          ) : data.items.map((g) => (
            <div key={g.group_id} className="panel p-5" data-testid={`group-${g.group_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-ui text-base font-bold text-slate-900">{g.name}</h3>
                  <p className="text-xs text-orange-700 mt-2" data-testid={`group-modules-${g.group_id}`}>Linked modules: {[...new Set(g.members.flatMap((m) => m.modules || []))].map((m) => m === "lab" ? "Laboratory" : m === "hospital" ? "Hospital" : "Pharmacy").join(" · ")}</p>
                  <p className="text-xs text-slate-500 mt-1">{g.members.length} member{g.members.length === 1 ? "" : "s"}</p>
                </div>
                <button onClick={() => dissolve(g.group_id)} className={ghostBtnCls} data-testid={`dissolve-${g.group_id}`}><Trash2 className="h-4 w-4" /> Dissolve</button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {g.members.map((m) => (
                  <div key={m.business_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`member-${m.business_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{m.name}</p>
                      <p className="text-xs text-slate-500">{TYPE_LABEL[m.business_type] || m.business_type}{m.city ? ` · ${m.city}` : ""}</p>
                    </div>
                    <button onClick={() => unlink(g.group_id, m.business_id)} className={ghostBtnCls} data-testid={`unlink-${m.business_id}`}><Unlink className="h-4 w-4" /> Unlink</button>
                  </div>
                ))}
              </div>
              {data.ungrouped.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Link a business:</span>
                  {data.ungrouped.map((b) => (
                    <button key={b.business_id} onClick={() => link(g.group_id, b.business_id)}
                      data-testid={`link-${g.group_id}-${b.business_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-900">
                      <Link2 className="h-3.5 w-3.5" /> {b.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="panel p-5">
            <h3 className="font-ui text-sm font-bold text-slate-900 flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> Ungrouped businesses</h3>
            <p className="text-xs text-slate-500 mt-1">These businesses are independent and not sharing identity with anyone.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.ungrouped.length === 0 ? <span className="text-sm text-slate-400">All businesses are grouped.</span> :
                data.ungrouped.map((b) => (
                  <span key={b.business_id} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700" data-testid={`ungrouped-${b.business_id}`}>{b.name}</span>
                ))}
            </div>
          </div>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New business group" testid="group-modal">
        <form onSubmit={create} className="space-y-5">
          <Field label="Group name"><input className={inputCls} data-testid="group-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AJ Classic Group" /></Field>
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-wider text-slate-700 mb-2">Add businesses (optional)</p>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
              {(data?.ungrouped || []).length === 0 ? <p className="p-4 text-sm text-slate-500">No ungrouped businesses available.</p> :
                data.ungrouped.map((b) => (
                  <label key={b.business_id} className="flex items-center gap-3 p-3 text-sm hover:bg-slate-50 cursor-pointer" data-testid={`pick-${b.business_id}`}>
                    <input type="checkbox" checked={picked.includes(b.business_id)} onChange={() => toggle(b.business_id)} />
                    <span><span className="font-medium text-slate-900">{b.name}</span> <span className="text-xs text-slate-500">· {TYPE_LABEL[b.business_type] || b.business_type}</span></span>
                  </label>
                ))}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-group-btn">{busy ? "Creating…" : "Create group"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
